import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { GraduationCap, Download, Layers, Loader2, Printer } from 'lucide-react';
import { useShellTitle } from '../components/layout/ShellTitleContext';
import Markdown from '../features/lecture/components/Markdown';

// Weeks that have a study pack in /public/study (kept in sync with content/study).
const WEEKS = [1, 2, 3, 4, 5, 6, 7, 8, 9];

// Study packs page: condensed, exam-focused revision material generated from the
// lecture narration — one pack per week (summary, key concepts, cheat sheet,
// flashcards, common pitfalls). The point is to let a learner grasp the course
// fast instead of watching ~100 hours of lecture. The .md files are served as
// static assets from /public/study (copied from content/study, the source of
// truth) and rendered with the shared Markdown component (GFM tables + KaTeX +
// inline glossary pronunciations). The combined flashcard deck is downloadable
// as a CSV for Anki / any spaced-repetition app. Reachable at /study + left nav.
export default function StudyPage() {
  useShellTitle('Study packs');
  const [week, setWeek] = useState<number>(WEEKS[0]);
  const [md, setMd] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch(`/study/week0${week}.md`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.text();
      })
      .then((text) => {
        if (cancelled) return;
        // Guard against a dev server returning index.html for a missing asset.
        if (/^\s*<!doctype html/i.test(text)) throw new Error('not found');
        setMd(text);
      })
      .catch(() => !cancelled && setError('Could not load this study pack.'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [week]);

  return (
    <div className="ws-page">
      <header className="mb-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-ws-accent-quiet text-ws-accent-ink">
              <GraduationCap size={18} />
            </span>
            <div>
              <h1 className="text-xl font-semibold text-ws-ink">Study packs</h1>
              <p className="text-[13px] text-ws-ink-3">
                Condensed revision — summary, cheat sheet &amp; flashcards per week.
                Grasp it fast instead of watching every slide.
              </p>
            </div>
          </div>
          <div className="hidden shrink-0 gap-2 sm:flex">
            <Link
              to={`/flashcards?week=${week}`}
              className="inline-flex items-center gap-1.5 rounded-lg bg-ws-accent px-3 py-2 text-[13px] font-medium text-ws-on-accent shadow-sm transition-colors hover:bg-ws-accent-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--ws-focus)]"
              title={`Study Week ${week} as interactive flashcards with spaced repetition`}
            >
              <Layers size={15} />
              Study as flashcards →
            </Link>
            <a
              href="/study/flashcards.csv"
              download="ace-chem-flashcards.csv"
              className="inline-flex items-center gap-1.5 rounded-lg border border-ws-border bg-ws-surface px-3 py-2 text-[13px] font-medium text-ws-ink-2 transition-colors hover:border-ws-accent hover:text-ws-accent"
              title="Download all 266 flashcards as a CSV (import into Anki)"
            >
              <Download size={15} />
              Anki deck
            </a>
            <button
              type="button"
              onClick={() => window.print()}
              className="inline-flex items-center gap-1.5 rounded-lg border border-ws-border bg-ws-surface px-3 py-2 text-[13px] font-medium text-ws-ink-2 transition-colors hover:border-ws-accent hover:text-ws-accent"
              title="Print this pack"
            >
              <Printer size={15} />
              Print
            </button>
          </div>
        </div>

        {/* Week selector */}
        <div className="mt-4 flex flex-wrap gap-1.5" role="tablist" aria-label="Study pack week">
          {WEEKS.map((w) => (
            <button
              key={w}
              type="button"
              role="tab"
              aria-selected={week === w}
              onClick={() => setWeek(w)}
              className={`rounded-lg px-3 py-1.5 text-[13px] font-medium transition-colors ${
                week === w
                  ? 'bg-ws-accent-quiet text-ws-accent-ink'
                  : 'border border-ws-border text-ws-ink-3 hover:bg-ws-surface-2'
              }`}
            >
              Week {w}
            </button>
          ))}
        </div>
      </header>

      {loading ? (
        <div className="flex items-center gap-2 py-16 text-sm text-ws-ink-3">
          <Loader2 size={16} className="animate-spin" />
          Loading week {week}…
        </div>
      ) : error ? (
        <p className="py-12 text-center text-sm text-ws-danger">{error}</p>
      ) : (
        <article className="rounded-xl border border-ws-tile-border bg-ws-tile p-5 shadow-sm sm:p-7">
          <Markdown glossary>{md}</Markdown>
        </article>
      )}
    </div>
  );
}
